import { ODATA_BASE } from "helpers/api_config";
import React, { useEffect, useState, useMemo } from "react";
import { Container, Row, Col, Card, CardBody, Badge, Nav, NavItem, NavLink, Alert } from "reactstrap";
import Breadcrumbs from "components/Common/Breadcrumb";
import SayfaRehberi from "../../../components/Common/SayfaRehberi";
import TableContainer from "../../../components/Common/TableContainer";
import axios from "axios";

/**
 * SATIN ALMA / GELIR RAPORU
 *
 * Panelde satin almalari gosteren hicbir ekran yoktu; "one cikan ilan" ve
 * "rehber reklami" gelirleri panelden hic takip edilemiyordu. Veri iki
 * tabloda duruyor ama hicbir yerde listelenmiyordu:
 *
 *   Job_Advertisement_Users_Complated      -> one cikan ILAN satin almalari
 *   Company_Advertisement_Users_Complated  -> one cikan REHBER kaydi satin almalari
 *
 * Iki tablo da ayni sekilde: Users_ID + baslangic/bitis tarihi.
 * Birim fiyat Purchase_Options tablosundan okunuyor (tek kayit, "Ucretli Ilan").
 *
 * Rapor sunlari gosteriyor:
 *   - toplam satin alma, su an AKTIF olan, suresi DOLMUS olan
 *   - tahmini toplam ve aktif gelir
 *   - aya gore dagilim (hangi ay kac satis)
 *   - satin alan kisiye gore liste (ad/telefon Users tablosundan eslestiriliyor)
 */

const JOB_URL = `${ODATA_BASE}/Job_Advertisement_Users_Complated?$orderby=ID desc`;
const COMPANY_URL = `${ODATA_BASE}/Company_Advertisement_Users_Complated?$orderby=ID desc`;
const USERS_URL = `${ODATA_BASE}/Users?$select=ID,Users_Name_Surname,Users_Nickname,Users_PhoneNumber`;
const OPTIONS_URL = `${ODATA_BASE}/Purchase_Options`;

const cek = (url) =>
  axios
    .get(url)
    .then((r) => {
      const d = r.data;
      return Array.isArray(d) ? d : (d && d.value) || [];
    })
    // Bir liste alinamazsa digerleri yine gosterilsin
    .catch(() => []);

const tarihYaz = (deger) => {
  if (!deger) return "-";
  const t = new Date(deger);
  return isNaN(t.getTime()) ? "-" : t.toLocaleDateString("tr-TR");
};

const ayAnahtari = (deger) => {
  if (!deger) return null;
  const t = new Date(deger);
  if (isNaN(t.getTime())) return null;
  return `${t.getFullYear()}-${String(t.getMonth() + 1).padStart(2, "0")}`;
};

/** Bitis tarihi gecmemisse satin alma hala aktiftir. */
const aktifMi = (kayit) => {
  if (!kayit.Complated_Finish_Date) return false;
  const bitis = new Date(kayit.Complated_Finish_Date);
  return !isNaN(bitis.getTime()) && bitis.getTime() > Date.now();
};

const paraYaz = (tutar) =>
  `${Number(tutar || 0).toLocaleString("tr-TR", { minimumFractionDigits: 2, maximumFractionDigits: 2 })} €`;

const SatinAlmaRaporu = () => {
  document.title = "Satın Alma Raporu | Berlin Esnaf";

  const [ilanlar, setIlanlar] = useState([]);
  const [rehberler, setRehberler] = useState([]);
  const [kullanicilar, setKullanicilar] = useState([]);
  const [birimFiyat, setBirimFiyat] = useState(null);
  const [yukleniyor, setYukleniyor] = useState(true);
  const [aktifSekme, setAktifSekme] = useState("ilan");

  useEffect(() => {
    let acik = true;
    Promise.all([cek(JOB_URL), cek(COMPANY_URL), cek(USERS_URL), cek(OPTIONS_URL)])
      .then(([j, c, u, o]) => {
        if (!acik) return;
        setIlanlar(j);
        setRehberler(c);
        setKullanicilar(u);
        const fiyat = o && o.length > 0 ? Number(o[0].Purchase_Options_Price) : null;
        setBirimFiyat(isNaN(fiyat) ? null : fiyat);
      })
      .finally(() => acik && setYukleniyor(false));
    return () => {
      acik = false;
    };
  }, []);

  const kullaniciMap = useMemo(() => {
    const m = {};
    kullanicilar.forEach((k) => {
      m[String(k.ID)] = k;
    });
    return m;
  }, [kullanicilar]);

  /** Ham kayitlara kullanici bilgisi ve durum ekler. */
  const zenginlestir = (liste) =>
    liste.map((k) => {
      const kisi = kullaniciMap[String(k.Users_ID)];
      return {
        ...k,
        _ad: kisi ? kisi.Users_Name_Surname || kisi.Users_Nickname || "-" : "(silinmiş kullanıcı)",
        _telefon: kisi ? kisi.Users_PhoneNumber || "-" : "-",
        _aktif: aktifMi(k),
      };
    });

  const ilanSatirlari = useMemo(() => zenginlestir(ilanlar), [ilanlar, kullaniciMap]);
  const rehberSatirlari = useMemo(() => zenginlestir(rehberler), [rehberler, kullaniciMap]);

  const toplam = ilanlar.length + rehberler.length;
  const aktifSayisi =
    ilanSatirlari.filter((x) => x._aktif).length + rehberSatirlari.filter((x) => x._aktif).length;
  const dolmusSayisi = toplam - aktifSayisi;

  // Aya gore dagilim (iki tur birlikte)
  const aylikDagilim = useMemo(() => {
    const harita = {};
    const ekle = (liste, alan) => {
      liste.forEach((k) => {
        const ay = ayAnahtari(k.Complated_Start_Date);
        if (!ay) return;
        if (!harita[ay]) harita[ay] = { ay, ilan: 0, rehber: 0, toplam: 0 };
        harita[ay][alan] += 1;
        harita[ay].toplam += 1;
      });
    };
    ekle(ilanlar, "ilan");
    ekle(rehberler, "rehber");
    return Object.values(harita).sort((a, b) => (a.ay < b.ay ? 1 : -1));
  }, [ilanlar, rehberler]);

  const tarihsizSayisi =
    ilanlar.filter((k) => !ayAnahtari(k.Complated_Start_Date)).length +
    rehberler.filter((k) => !ayAnahtari(k.Complated_Start_Date)).length;

  const kolonlar = useMemo(
    () => [
      { Header: "#", accessor: "ID", filterable: true },
      { Header: "Satın Alan", accessor: "_ad", filterable: true },
      { Header: "Telefon", accessor: "_telefon", filterable: true },
      {
        Header: "Başlangıç",
        accessor: "Complated_Start_Date",
        Cell: (c) => <span>{tarihYaz(c.row.original.Complated_Start_Date)}</span>,
      },
      {
        Header: "Bitiş",
        accessor: "Complated_Finish_Date",
        Cell: (c) => <span>{tarihYaz(c.row.original.Complated_Finish_Date)}</span>,
      },
      {
        Header: "Durum",
        accessor: "_aktif",
        Cell: (c) =>
          c.row.original._aktif ? (
            <Badge color="success">Aktif</Badge>
          ) : (
            <Badge color="secondary">Süresi doldu</Badge>
          ),
      },
    ],
    []
  );

  const aylikKolonlar = useMemo(
    () => [
      { Header: "Ay", accessor: "ay", filterable: true },
      { Header: "Öne Çıkan İlan", accessor: "ilan" },
      { Header: "Rehber Reklamı", accessor: "rehber" },
      {
        Header: "Toplam",
        accessor: "toplam",
        Cell: (c) => <strong>{c.row.original.toplam}</strong>,
      },
      {
        Header: "Tahmini Gelir",
        accessor: "_gelir",
        Cell: (c) => (
          <span>{birimFiyat == null ? "-" : paraYaz(c.row.original.toplam * birimFiyat)}</span>
        ),
      },
    ],
    [birimFiyat]
  );

  const KutuKart = ({ baslik, deger, altYazi, ikon, renk }) => (
    <Col md="3">
      <Card className="mini-stats-wid">
        <CardBody>
          <div className="d-flex">
            <div className="flex-grow-1">
              <p className="text-muted fw-medium mb-2">{baslik}</p>
              <h4 className="mb-0">{deger}</h4>
              {altYazi && <p className="text-muted mb-0 mt-1 font-size-12">{altYazi}</p>}
            </div>
            <div className="avatar-sm rounded-circle bg-primary align-self-center mini-stat-icon">
              <span className={"avatar-title rounded-circle bg-" + renk}>
                <i className={"bx " + ikon + " font-size-24"} />
              </span>
            </div>
          </div>
        </CardBody>
      </Card>
    </Col>
  );

  const aktifSatirlar = aktifSekme === "ilan" ? ilanSatirlari : rehberSatirlari;

  return (
    <React.Fragment>
      <div className="page-content">
        <Container fluid>
          <Breadcrumbs title="Berlin Esnaf" breadcrumbItem="Satın Alma Raporu" />
          <SayfaRehberi sayfa="satinalmaraporu" />

          <Row>
            <KutuKart
              baslik="Toplam Satın Alma"
              deger={toplam}
              altYazi={`${ilanlar.length} ilan · ${rehberler.length} rehber`}
              ikon="bx-cart"
              renk="primary"
            />
            <KutuKart
              baslik="Şu An Aktif"
              deger={aktifSayisi}
              altYazi="Süresi devam eden"
              ikon="bx-check-circle"
              renk="success"
            />
            <KutuKart
              baslik="Süresi Dolmuş"
              deger={dolmusSayisi}
              altYazi="Yenileme fırsatı"
              ikon="bx-time"
              renk="warning"
            />
            <KutuKart
              baslik="Tahmini Toplam Gelir"
              deger={birimFiyat == null ? "-" : paraYaz(toplam * birimFiyat)}
              altYazi={birimFiyat == null ? "Birim fiyat okunamadı" : `Birim: ${paraYaz(birimFiyat)}`}
              ikon="bx-euro"
              renk="info"
            />
          </Row>

          <Alert color="info" className="mb-4">
            <i className="bx bx-info-circle align-middle me-1" />
            Gelir rakamları, <strong>Purchase_Options</strong> tablosundaki tek birim fiyat
            {birimFiyat != null && <> (<strong>{paraYaz(birimFiyat)}</strong>)</>} ile satın alma
            adedinin çarpımıdır; <strong>tahminidir</strong>. Mağaza kesintileri (Apple/Google
            komisyonu), iadeler ve kampanyalı satışlar bu hesaba dâhil değildir.
            {tarihsizSayisi > 0 && (
              <>
                {" "}
                Ayrıca <strong>{tarihsizSayisi}</strong> kaydın başlangıç tarihi boş olduğu için
                aylık dağılıma girmiyor.
              </>
            )}
          </Alert>

          <Row>
            <Col lg="12">
              <Card>
                <CardBody>
                  <h5 className="mb-3">Aylara Göre Satın Almalar</h5>
                  {yukleniyor ? (
                    <p className="text-muted">Yükleniyor...</p>
                  ) : aylikDagilim.length === 0 ? (
                    <p className="text-muted">Tarihi olan satın alma kaydı bulunamadı.</p>
                  ) : (
                    <TableContainer
                      columns={aylikKolonlar}
                      data={aylikDagilim}
                      isGlobalFilter={true}
                      customPageSize={12}
                      className="custom-header-css"
                    />
                  )}
                </CardBody>
              </Card>
            </Col>
          </Row>

          <Row>
            <Col lg="12">
              <Card>
                <CardBody>
                  <Nav tabs className="nav-tabs-custom mb-3">
                    <NavItem>
                      <NavLink
                        style={{ cursor: "pointer" }}
                        className={aktifSekme === "ilan" ? "active" : ""}
                        onClick={() => setAktifSekme("ilan")}
                      >
                        <i className="bx bx-briefcase align-middle me-1" />
                        Öne Çıkan İlanlar{" "}
                        <Badge color="primary" className="ms-1">
                          {ilanlar.length}
                        </Badge>
                      </NavLink>
                    </NavItem>
                    <NavItem>
                      <NavLink
                        style={{ cursor: "pointer" }}
                        className={aktifSekme === "rehber" ? "active" : ""}
                        onClick={() => setAktifSekme("rehber")}
                      >
                        <i className="bx bx-buildings align-middle me-1" />
                        Rehber Reklamları{" "}
                        <Badge color="info" className="ms-1">
                          {rehberler.length}
                        </Badge>
                      </NavLink>
                    </NavItem>
                  </Nav>

                  {yukleniyor ? (
                    <p className="text-muted">Yükleniyor...</p>
                  ) : aktifSatirlar.length === 0 ? (
                    <p className="text-muted">Bu türde satın alma kaydı yok.</p>
                  ) : (
                    <TableContainer
                      columns={kolonlar}
                      data={aktifSatirlar}
                      isGlobalFilter={true}
                      customPageSize={15}
                      className="custom-header-css"
                    />
                  )}
                </CardBody>
              </Card>
            </Col>
          </Row>
        </Container>
      </div>
    </React.Fragment>
  );
};

export default SatinAlmaRaporu;
